import React, { useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/zh-cn';
import i18next from 'i18next';
import zhConvertor from 'zhconvertor';
import { IoClose } from 'react-icons/io5';
import { Gpt } from 'react-gpt-ads';
import CandidateBar from './CandidateBar';
import SeatDemographics from './SeatDemographics';
import { getBgColorByParty } from './seatHelpers';
import { trackEvent } from '../../utils/analytics';
import './SeatInfoPanel.css';

const tr = (text) =>
  localStorage.getItem('lang') !== 'zh_TW' ? zhConvertor.t2s(text) : text;

const SeatInfoPanel = ({ seat, seatName, onClose, onViewDetail }) => {
  const open = Boolean(seatName);
  const [mounted, setMounted] = useState(false);
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(false);

  useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => setMounted(true));
      return () => cancelAnimationFrame(id);
    }
    setMounted(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const tag = seat && seat.ednamec ? seat.ednamec : null;
  useEffect(() => {
    if (!tag) {
      setArticles([]);
      return;
    }
    let cancelled = false;
    setArticlesLoading(true);
    setArticles([]);
    trackEvent('article_tag', { tag, location: 'seat_panel' });
    const lang = localStorage.getItem('lang') || 'zh_CN';
    axios
      .get(`article_tag.php?tag=${encodeURIComponent(tag)}&lang=${lang}`, {
        timeout: 7000,
      })
      .then((response) => {
        if (cancelled) return;
        const data = response.data;
        if (data && data.status === 'success' && Array.isArray(data.articles)) {
          setArticles(data.articles);
        } else {
          setArticles([]);
        }
      })
      .catch(() => {
        if (!cancelled) setArticles([]);
      })
      .finally(() => {
        if (!cancelled) setArticlesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tag]);

  if (!open) return null;

  const winner =
    seat && Array.isArray(seat.candidate)
      ? seat.candidate.find((c) => c.status == 1)
      : null;
  const accent = winner ? getBgColorByParty(winner.alliance_en) : '#cb0b12';

  return (
    <>
      <div
        className={`seat_panel_backdrop ${mounted ? 'open' : ''}`}
        onClick={onClose}
      />
      <div
        className={`seat_panel ${mounted ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="seat_panel_handle" />
        <div className="seat_panel_header" style={{ borderTopColor: accent }}>
          <div className="seat_panel_title">
            {seat ? (
              <>
                <span className="seat_panel_seatname">{seat.seatname}</span>
                <span className="seat_panel_edname">
                  {seat.ednamec} {seat.edname}
                </span>
              </>
            ) : (
              <span className="seat_panel_seatname">{seatName}</span>
            )}
          </div>
          {onViewDetail && seat && (
            <button
              type="button"
              className="seat_panel_detail_btn"
              onClick={() => {
                trackEvent('seat_panel_detail_click', { seat: seat.seatname });
                onViewDetail();
              }}
            >
              {tr('查看该议席')} →
            </button>
          )}
          <button
            type="button"
            className="seat_panel_close"
            aria-label="Close"
            onClick={onClose}
          >
            <IoClose />
          </button>
        </div>

        <div className="seat_panel_body">
          {!seat ? (
            <div className="seat_panel_empty">{tr('暂无资料')}</div>
          ) : (
            <>
              {window.hideCurrentContestant === false && seat.is_o == '0' && parseInt(seat.total_votes) > 0 && (
                <h3 className="hint_unofficial">*{tr('非官方成績')}</h3>
              )}

              {seat.showinfo === true && (
                <SeatDemographics
                  regPercent={seat.reg_percent}
                  electorate={seat.electorate}
                />
              )}

              {window.hideCurrentContestant === false && seat.candidate && seat.candidate.length > 0 && (
                <div className="seat_info_grid">
                  <div className="seat_info_left">{tr('投票情况')}</div>
                  <div className="seat_info_right">
                    {parseInt(seat.total_votes) > 0 && (
                      <>
                        <span className="info_numbers">
                          {tr('投票人数')}: {seat.total_votes.toLocaleString()}
                          {parseFloat(seat.percentage) > 0 && (
                            <span>{' (' + seat.percentage + '%)'}</span>
                          )}
                        </span>
                        <br />
                      </>
                    )}
                    {parseInt(seat.majority) > 0 && (
                      <span className="info_numbers">
                        {tr('多数票')}: {parseInt(seat.majority).toLocaleString()}
                      </span>
                    )}
                    {parseInt(seat.invalid) > 0 && (
                      <span className="info_numbers">
                        {tr('废票')}: {parseInt(seat.invalid).toLocaleString()}
                      </span>
                    )}
                    <div className="seat_panel_ad_slot">
                      <Gpt
                        adUnit="/2163006/election_seat_panel"
                        name="div-gpt-ad-1782186174721-0"
                        size={[[300, 250], 'fluid', [336, 280], [320, 100]]}
                      />
                    </div>
                    <div className="bar_chart_container">
                      {seat.candidate.map((c, idx) => (
                        <CandidateBar
                          key={idx}
                          candidate={c}
                          totalVotes={seat.total_votes}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {(articlesLoading || articles.length > 0) && (
                <div className="seat_panel_articles">
                  <div className="seat_panel_articles_title">
                    {tr('相关报道')}
                  </div>
                  {articlesLoading && articles.length === 0 ? (
                    <div className="seat_panel_articles_loading">
                      {tr('载入中…')}
                    </div>
                  ) : (
                    <>
                      <ul className="seat_panel_articles_list">
                        {articles.map((art) => (
                          <li
                            key={art.id}
                            className="seat_panel_article_item"
                          >
                            <a
                              href={art.url}
                              target="_blank"
                              rel="noreferrer"
                              className="seat_panel_article_link"
                            >
                              {art.picurl && (
                                <img
                                  src={art.picurl}
                                  alt=""
                                  className="seat_panel_article_thumb"
                                  loading="lazy"
                                />
                              )}
                              <div className="seat_panel_article_text">
                                <div className="seat_panel_article_title">
                                  {art.title}
                                  {art.type === 'gallery' && (
                                    <span className="seat_panel_article_tag">
                                      {i18next.t('图集')}
                                    </span>
                                  )}
                                </div>
                                {art.date && (
                                  <div className="seat_panel_article_date">
                                    {moment(art.date).fromNow()}
                                  </div>
                                )}
                              </div>
                            </a>
                          </li>
                        ))}
                      </ul>
                      <a
                        href={`https://www.orientaldaily.com.my/news/browse/${encodeURIComponent(seat.ednamec)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="seat_panel_articles_more"
                      >
                        {tr(`更多 ${seat.ednamec} 相关新闻`)} →
                      </a>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SeatInfoPanel;
